import Breadcrumb from "components/Common/Breadcrumb";
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Col, Label, Row } from "reactstrap";
import moment from "moment-timezone";
import { AdminService } from "services/AdminService";
import TableContainer from "components/Common/TableContainer";
import DriverInvoiceView from "./DriverInvoiceView";
import { toast } from "react-toastify";
toast.configure();
import { Puff } from "react-loader-spinner";

interface DriverSummary {
  _id: string;
  driverId: string;
  customerId: string;
  driverName: string;
  percentage: number;
  currentWeek: {
    totalCommission: number;
    totalOrderValue: number;
    orderCount: number;
    totalActiveSeconds: number;
    orderDetails: any[];
  };
  weekNumber: number;
  year: number;
}

const DriverBilling: React.FC = () => {
  const getCurrentWeek = () => {
    const now = moment.tz("America/Toronto");
    const startOfWeek = now.clone().startOf("isoWeek");
    const endOfWeek = now.clone().endOf("isoWeek");
    return { startOfWeek, endOfWeek };
  };

  const { startOfWeek, endOfWeek } = getCurrentWeek();

  const [driverSummaries, setDriverSummaries] = useState<DriverSummary[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<any>(null);
  const [isloading, setIsloading] = useState<boolean>(true);
  const [modal, setModal] = useState(false);
  const [LIMIT_P_C, setLIMIT_P_C] = useState(10);
  const [isOffset_p_c, setOffset_p_c] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1);

  /** Normalize API response */
  const extractData = (res: any): DriverSummary[] => {
    if (Array.isArray(res)) return res;
    if (res?.success && Array.isArray(res.data)) return res.data;

    const keys = Object.keys(res.data || {});
    for (const key of keys) {
      if (Array.isArray(res.data[key])) return res.data[key];
    }

    return [];
  };

  useEffect(() => {
    setIsloading(true);

    AdminService.getLatestDriverBilling()
      .then((res) => {
        const data = extractData(res);
        setDriverSummaries(data);
        setPageCount(Math.ceil(data.length / LIMIT_P_C));
      })
      .catch(() => toast.error("Failed to load driver earnings"))
      .finally(() => setIsloading(false));
  }, [LIMIT_P_C]);

  const SeeMore = (page: any) => {
    setOffset_p_c(page.selected + 1);
  };

  const handleColumnChange = (selectedOption: any) => {
    setLIMIT_P_C(selectedOption.value);
    setPageCount(Math.ceil(driverSummaries.length / selectedOption.value));
    setOffset_p_c(1);
  };

  const searchTable = (text: string) => {
    if (!text) {
      AdminService.getLatestDriverBilling().then((res) => {
        setDriverSummaries(extractData(res));
      });
      return;
    }

    const filtered = driverSummaries.filter(
      (driver) =>
        driver.driverName.toLowerCase().includes(text.toLowerCase()) ||
        driver.customerId?.toLowerCase().includes(text.toLowerCase())
    );

    setDriverSummaries(filtered);
    setOffset_p_c(1);
  };

  const formatActiveHours = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  };


  const handleViewInvoice = (row: any) => {
    const d = row.original;

    const invoicePayload = {
      _id: d._id,
      zuluId: `INV-${d.year}-W${d.weekNumber
        .toString()
        .padStart(2, "0")}-${d.customerId || "N/A"}`,
      totalOrders: d.currentWeek.orderCount,
      totalOrderAmount: d.currentWeek.totalOrderValue,
      totalCommision: d.currentWeek.totalCommission,
      orderDetails: d.currentWeek.orderDetails,
      driverDetails: {
        customerId: d.customerId,
        firstName: d.driverName,
        lastName: "",
        percentage: d.percentage,
        _id: d.driverId || d._id,
      },
      previousWeek: null
    };

    setSelectedDriver(invoicePayload);
    setModal(true);
  };

  const columns = useMemo(
    () => [
      {
        id: "customerId",
        Header: "Customer ID",
        accessor: "customerId",
      },
      {
        id: "driverName",
        Header: "Driver Name",
        accessor: "driverName",
      },
      {
        id: "percentage",
        Header: "Commission Rate",
        accessor: "percentage",
        Cell: ({ value }: any) => `${value}%`,
      },
      {
        id: "orderCount",
        Header: "Orders",
        accessor: "currentWeek.orderCount",
        Cell: ({ value }: any) => value.toString().padStart(2, "0"),
      },
      {
        id: "totalOrderValue",
        Header: "Order Value",
        accessor: "currentWeek.totalOrderValue",
        Cell: ({ value }: any) => `$${value.toFixed(2)}`,
      },
      {
        id: "totalCommission",
        Header: "Commission",
        accessor: "currentWeek.totalCommission",
        Cell: ({ value }: any) => `$${value.toFixed(2)}`,
      },
      {
        id: "activeHours",
        Header: "Active Hours",
        accessor: "currentWeek.totalActiveSeconds",
        Cell: ({ value }: any) => formatActiveHours(value),
      },
      {
        id: "action",
        Header: "Action",
        Cell: ({ row }: any) => (
          <div className="d-flex justify-content-center">
            <button
              className="btn btn-primary btn-sm px-3"
              onClick={() => handleViewInvoice(row)}
            >
              View Details
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const paginatedData = useMemo(() => {
    const start = (isOffset_p_c - 1) * LIMIT_P_C;
    return driverSummaries.slice(start, start + LIMIT_P_C);
  }, [driverSummaries, isOffset_p_c, LIMIT_P_C]);

  return (
    <React.Fragment>
      <div className="page-content mb-5">
        <div className="container-fluid mb-5">

          <Breadcrumb title="Driver Earnings" breadcrumbItem="Current Week Summary" />

          {/* Summary Header */}
          <Row className="mb-4">
            <Col lg="12">
              <div className="p-3 bg-light rounded d-flex justify-content-between">
                <div><strong>Total Drivers:</strong> {driverSummaries.length}</div>
                <div><strong>Total Orders:</strong> {driverSummaries.reduce((s, d) => s + d.currentWeek.orderCount, 0)}</div>
                <div><strong>Total Commission:</strong> ${driverSummaries.reduce((s, d) => s + d.currentWeek.totalCommission, 0).toFixed(2)}</div>
                <div><strong>Total Order Value:</strong> ${driverSummaries.reduce((s, d) => s + d.currentWeek.totalOrderValue, 0).toFixed(2)}</div>
              </div>
            </Col>
          </Row>

          {/* Driver Table */}
          <Row>
            <Col lg="12">
              <Card className="mb-5">
                <CardBody>
                  <Label>
                    Week {startOfWeek.isoWeek()} ({startOfWeek.format('MMM D')} - {endOfWeek.format('MMM D')})
                  </Label>

                  {isloading ? (
                    <div className="d-flex justify-content-center" style={{ height: 400 }}>
                      <Puff height={60} width={60} />
                    </div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={paginatedData}
                      currentPage={isOffset_p_c}
                      pagelimit={pageCount}
                      handlePageChange={SeeMore}
                      handleColumnChange={handleColumnChange}
                      selectedColumns={LIMIT_P_C}
                      showColumnChange={true}
                      handleSearch={searchTable}
                      isGlobalFilter={true}
                      customPageSize={50}
                      className="custom-header-css"
                      hasCheckBox={false} ispopup={undefined} isJobListGlobalFilter={undefined} isAddOptions={undefined} isAddUserList={undefined} handleOrderClicks={undefined} handleUserClick={undefined} handleCustomerClick={undefined} isAddCustList={undefined} isPDFGenerate={undefined} isAddressBook={undefined} hancleDeleteRecords={undefined} handleSelect={undefined} selectedID={undefined} handleSendEmail={undefined} footerData={undefined} />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Invoice Modal */}
          <DriverInvoiceView
            isOpen={modal}
            handlePopup={() => setModal(false)}
            driverBill={selectedDriver}
            dates={{ date1: startOfWeek, date2: endOfWeek }}
          />

        </div>
      </div>
    </React.Fragment>
  );
};

export default DriverBilling;
